import type {
  CreateManagedClientDto,
  CreateManagedClientResponse,
} from '@shared/dto/consultant/create-managed-client.dto';
import { inject } from 'inversify';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';
import type {
  AdminConsultantClient,
  AdminConsultantClientsPage,
  AdminConsultantClientsParams,
  ConsultantAccessResponse,
  ConsultantClientCommissions,
  ConsultantClientLogo,
  ConsultantClientsPage,
  ConsultantClientsParams,
  ConsultantPartnerDashboard,
  IConsultantService,
} from './consultant.service.interface';

@Injectable()
export class ConsultantService implements IConsultantService {
  constructor(@inject(HttpService) private readonly http: IHttpService) {}

  async listClients(params: ConsultantClientsParams): Promise<ConsultantClientsPage> {
    const search = new URLSearchParams();
    search.set('page', String(params.page));
    search.set('pageSize', String(params.pageSize));
    const term = params.search?.trim();
    if (term) {
      search.set('search', term);
    }
    return this.http.get<ConsultantClientsPage>(`consultant/clients?${search.toString()}`);
  }

  async hasConsultantAccess(): Promise<ConsultantAccessResponse> {
    return this.http.get<ConsultantAccessResponse>('consultant/access');
  }

  async getPartnerDashboard(): Promise<ConsultantPartnerDashboard> {
    return this.http.get<ConsultantPartnerDashboard>('consultant/partner');
  }

  async getClientCommissions(
    tenantId: string,
    page: number,
    pageSize: number,
  ): Promise<ConsultantClientCommissions> {
    const search = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    return this.http.get<ConsultantClientCommissions>(
      `consultant/clients/${tenantId}/commissions?${search.toString()}`,
    );
  }

  async listClientsForConsultant(
    userId: string,
    params: AdminConsultantClientsParams,
  ): Promise<AdminConsultantClientsPage> {
    const search = new URLSearchParams();
    search.set('page', String(params.page));
    search.set('pageSize', String(params.pageSize));
    if (params.includeRevoked) {
      search.set('includeRevoked', 'true');
    }
    return this.http.get<AdminConsultantClientsPage>(
      `admin/consultants/${userId}/clients?${search.toString()}`,
    );
  }

  async revokeClient(userId: string, tenantId: string): Promise<AdminConsultantClient> {
    return this.http.post<AdminConsultantClient>(
      `admin/consultants/${userId}/clients/${tenantId}/revoke`,
    );
  }

  async assignClient(
    userId: string,
    tenantId: string,
    partnerProfileId?: string,
  ): Promise<AdminConsultantClient> {
    return this.http.post<AdminConsultantClient>(
      `admin/consultants/${userId}/clients/${tenantId}/assign`,
      partnerProfileId ? { partnerProfileId } : {},
    );
  }

  async grantClientAccess(
    userId: string,
    tenantId: string,
    partnerProfileId?: string,
  ): Promise<AdminConsultantClient> {
    return this.http.post<AdminConsultantClient>(
      `admin/consultants/${userId}/clients/${tenantId}/grant-access`,
      partnerProfileId ? { partnerProfileId } : {},
    );
  }

  async createManagedClient(dto: CreateManagedClientDto): Promise<CreateManagedClientResponse> {
    return this.http.post<CreateManagedClientResponse>('consultant/clients', dto);
  }

  async deleteManagedClient(tenantId: string): Promise<void> {
    await this.http.delete(`consultant/clients/${tenantId}`);
  }

  async uploadClientLogo(tenantId: string, file: File): Promise<ConsultantClientLogo> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.putForm<ConsultantClientLogo>(`consultant/clients/${tenantId}/logo`, formData);
  }

  async deleteClientLogo(tenantId: string): Promise<ConsultantClientLogo> {
    return this.http.delete<ConsultantClientLogo>(`consultant/clients/${tenantId}/logo`);
  }

  async unassignClient(userId: string, tenantId: string): Promise<AdminConsultantClient> {
    return this.http.post<AdminConsultantClient>(
      `admin/consultants/${userId}/clients/${tenantId}/unassign`,
    );
  }
}
